import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

/** Draft cho popup contact — giữ trong session SPA, mất khi F5 */
export type ContactFormDraft = {
  name: string;
  companyName: string;
  companyWebsite: string;
  companySize: string;
  email: string;
  phone: string;
  jobTitle: string;
  interest: string;
  subject: string;
  message: string;
};

export const EMPTY_CONTACT_DRAFT: ContactFormDraft = {
  name: "",
  companyName: "",
  companyWebsite: "",
  companySize: "",
  email: "",
  phone: "",
  jobTitle: "",
  interest: "",
  subject: "",
  message: "",
};

type ContactChatContextValue = {
  contactOpen: boolean;
  openContactPopup: () => void;
  openContactInquiry: (subject: string, message: string) => void;
  closeContactPopup: () => void;
  toggleContactPopup: () => void;
  /** Chỉ dùng cho ContactForm embedded popup */
  contactDraft: ContactFormDraft;
  setContactDraft: React.Dispatch<React.SetStateAction<ContactFormDraft>>;
  resetContactDraft: () => void;
};

const ContactChatContext = createContext<ContactChatContextValue | null>(null);

export function ContactChatProvider({ children }: { children: ReactNode }) {
  const [contactOpen, setContactOpen] = useState(false);
  const [contactDraft, setContactDraft] = useState<ContactFormDraft>(() => ({ ...EMPTY_CONTACT_DRAFT }));

  const openContactPopup = useCallback(() => setContactOpen(true), []);
  const openContactInquiry = useCallback((subject: string, message: string) => {
    setContactDraft((previous) => ({
      ...previous,
      subject,
      message,
    }));
    setContactOpen(true);
  }, []);
  const closeContactPopup = useCallback(() => setContactOpen(false), []);
  const toggleContactPopup = useCallback(() => setContactOpen((v) => !v), []);
  const resetContactDraft = useCallback(() => setContactDraft({ ...EMPTY_CONTACT_DRAFT }), []);

  const value = useMemo(
    () => ({
      contactOpen,
      openContactPopup,
      openContactInquiry,
      closeContactPopup,
      toggleContactPopup,
      contactDraft,
      setContactDraft,
      resetContactDraft,
    }),
    [
      contactOpen,
      openContactPopup,
      openContactInquiry,
      closeContactPopup,
      toggleContactPopup,
      contactDraft,
      resetContactDraft,
    ]
  );

  return <ContactChatContext.Provider value={value}>{children}</ContactChatContext.Provider>;
}

export function useContactChat(): ContactChatContextValue {
  const ctx = useContext(ContactChatContext);
  if (!ctx) {
    throw new Error("useContactChat must be used within ContactChatProvider");
  }
  return ctx;
}
